import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoginToWebChatSystem } from '../services/api';
import { API_URLS } from '../constants/apiUrls';
import { LOCAL_AUTH_CACHE_KEY } from '../utils/authUtils';
import { getJwtExpiryMs, toMillis } from '../utils/tokenExpiry';
import { useAppEnv } from '../context/AppEnvContext';
import EventScribeSpinner from './shared/EventScribeSpinner';
import '../styles/LoginScreen.css';
// Import the whole library
import CryptoJS from 'crypto-js';


const LoginScreen = () => {
    const [formData, setFormData] = useState({
        accountId: '',
        clientId: '',
        eventId: '',
        // Optional: lets local testing open chat on one attendee, as the website's "Message" links do
        accountIdTo: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const env = useAppEnv();

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        // Only allow positive integers
        if (value === '' || (/^\d+$/.test(value) && parseInt(value) > 0)) {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        
        // Validation
        if (!formData.accountId || !formData.clientId || !formData.eventId) {
            setError('Event ID, Client ID, and Account ID are required');
            return;
        }

        setLoading(true);
        setError('');

        try {
        
             // Create hash
            const hashData = `${formData.eventId}-${formData.accountId}-Avengers`;
            const hash = CryptoJS.SHA256(hashData).toString();
  // ✅ Format data as expected by api.js
            // The API expects a string that will be appended as 'data' field
            const loginData = JSON.stringify({
                eventid: formData.eventId,
                clientid: formData.clientId, // Include clientId
                accountid: formData.accountId,
                accountidto: formData.accountIdTo || '',
                hash: hash
            });
  

             console.log('Sending login data:', loginData);

            // Call the login API
            const loginResponse = await LoginToWebChatSystem({
                url: API_URLS.LOGIN,
                data: loginData, // ✅ Pass as string
                domain: env.REACT_APP_DOMAIN
            });

            if (!loginResponse || !loginResponse.AccessToken) {
                throw new Error('Login failed - Invalid response');
            }

           // Store auth data in global window object
            const authTokenData = {
                token: loginResponse.AccessToken,
                clientId: formData.clientId,
                eventId: formData.eventId,
                accountId: formData.accountId,
                accountIdTo: loginResponse.AccountIDTo || null,
                IsLoggedIn: true,
                // The expiry the login API sends, else the access token's own exp
                expires: toMillis(loginResponse.AccessTokenExpiresAt) ?? getJwtExpiryMs(loginResponse.AccessToken)
            };

            window.__authTokenData__ = authTokenData;

            // Create and encrypt token data
            const tokenData = `${formData.eventId}_${formData.clientId}_${formData.accountId}`;
            const encryptedToken = CryptoJS.AES.encrypt(tokenData, env.REACT_APP_DOMAIN).toString();
            
            // URL encode the encrypted token
            const encodedToken = encodeURIComponent(encryptedToken);
            
            // Create URL with query parameters
            const parentOrigin = window.location.origin; // localhost:3000
            const timestamp = Date.now() + Math.random().toString(36).substr(2, 9);
            
            // Store in localStorage if localhost
            if (parentOrigin.includes('localhost') || parentOrigin.includes('127.0.0.1')) {
                const cacheData = {
                    ...authTokenData,
                    originalToken: tokenData,
                    encryptedToken: encryptedToken,
                    cachedAt: Date.now()
                };
                localStorage.setItem(LOCAL_AUTH_CACHE_KEY, JSON.stringify(cacheData));
                console.log('Token cached for localhost');
            }
            
            // Navigate with encrypted token and query parameters
            const chatUrl = `/chat/${encodedToken}?parentOrigin=${encodeURIComponent(parentOrigin)}&timestamp=${timestamp}`;
            console.log('Navigating to:', chatUrl);
            
            navigate(chatUrl);

        } catch (error) {
            console.error('Login error:', error);
            setError(error.message || 'Login failed. Please check your credentials.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-screen">
            <div className="login-container">
                <div className="login-header">
                    <img src="/logo.svg" alt="EventScribe" className="login-logo" />
                    <h1>Chat Login</h1>
                    <p>Enter your credentials to access the chat</p>
                </div>

                <form onSubmit={handleLogin} className="login-form">
                    <div className="form-group">
                        <label htmlFor="eventId">Event ID</label>
                        <input
                            type="text"
                            id="eventId"
                            name="eventId"
                            value={formData.eventId}
                            onChange={handleInputChange}
                            placeholder="Enter Event ID"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="clientId">Client ID</label>
                        <input
                            type="text"
                            id="clientId"
                            name="clientId"
                            value={formData.clientId}
                            onChange={handleInputChange}
                            placeholder="Enter Client ID"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="accountId">Account ID</label>
                        <input
                            type="text"
                            id="accountId"
                            name="accountId"
                            value={formData.accountId}
                            onChange={handleInputChange}
                            placeholder="Enter Account ID"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="accountIdTo">Account ID To (optional)</label>
                        <input
                            type="text"
                            id="accountIdTo"
                            name="accountIdTo"
                            value={formData.accountIdTo}
                            onChange={handleInputChange}
                            placeholder="Enter the other attendee's Account ID"
                        />
                    </div>

                    {error && <div className="error-message">{error}</div>}

                    <button 
                        type="submit" 
                        className="login-button"
                        disabled={loading || !formData.accountId || !formData.clientId || !formData.eventId}
                    >
                        {loading ? 'Logging in...' : 'Launch Chat'}
                    </button>
                </form>
            </div>

            {loading && <EventScribeSpinner message="Authenticating..." />}
        </div>
    );
};

export default LoginScreen;